import type {ExtractPropsWithoutRef} from '@/types';
import type {HotelCategoryType} from '@travel-frontend/ui-components';

import styled from '@emotion/styled';
import {
  Button,
  Flex,
  LabelMediumBold,
  PictureFallback,
} from '@travel-frontend/ui-components';
import {forwardRef} from 'react';
import {useTranslation} from 'react-i18next';

import {HotelCategory} from '@/components/HotelCategory';
import {GalleryContainer} from '@/components/HotelTileMedium/components/GalleryContainer';
import {HotelTileMediumLayout} from '@/components/HotelTileMediumLayout';
import {ImageCarousel} from '@/components/ImageCarousel';
import {PriceTag} from '@/components/PriceTag';
import {useSwipeableImages} from '@/hooks/useSwipeableImages';
import {RatingSummary} from '@/modules/common/components/RatingSummary';
import {type DiscountType, type Price} from '@/redux/v1/models';
import {testId} from '@/utils/testId';

type Props = Omit<
  ExtractPropsWithoutRef<typeof HotelTileMediumLayout>,
  | 'imageElement'
  | 'categoryElement'
  | 'nameElement'
  | 'priceElement'
  | 'ctaElement'
> & {
  imageUrls: Array<string>;
  name: string;
  category?: null | number;
  categoryType?: null | HotelCategoryType;
  ratingScore?: null | number;
  ratingCount?: null | number;
  recommendationRate?: null | number;
  totalPrice: Price;
  totalPriceInternal?: Price | null;
  totalPriceOriginal: Price;
  totalDiscount: Price;
  totalDiscountOriginal?: Price;
  pricePerPerson: Price;
  discountType: DiscountType;
  discountPercentage: number;
  totalDiscountHint?: string;
  canShowPriceCache: boolean;
};

export const HotelTileMedium = styled(
  forwardRef<HTMLDivElement, Props>(function HotelTileMedium(
    {
      imageUrls,
      name,
      category,
      categoryType,
      ratingScore,
      ratingCount,
      recommendationRate,
      totalPrice,
      totalPriceInternal,
      totalPriceOriginal,
      totalDiscount,
      totalDiscountOriginal,
      pricePerPerson,
      discountType,
      discountPercentage,
      totalDiscountHint,
      canShowPriceCache,
      ...restProps
    },
    ref
  ) {
    const {t} = useTranslation('hotels');

    const {goBack, goForward, getInternalProps} = useSwipeableImages({
      imageCount: imageUrls.length,
      imagesPerSlide: 1,
    });
    const hasRating =
      ratingScore != null &&
      ratingCount != null &&
      ratingScore >= 0 &&
      ratingCount > 0;

    return (
      <HotelTileMediumLayout
        {...restProps}
        ref={ref}
        imageElement={
          <GalleryContainer withRating={false}>
            {imageUrls.length > 0 ? (
              <ImageCarousel
                {...getInternalProps()}
                images={imageUrls}
                hasMouseEvents={false}
                onClickPrev={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  goBack();
                }}
                onClickNext={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  goForward();
                }}
              />
            ) : (
              <PictureFallback />
            )}
          </GalleryContainer>
        }
        categoryElement={
          <HotelCategory
            value={category}
            type={categoryType}
            data-testid={testId('hotel-category')}
          />
        }
        nameElement={
          <Flex flexDirection="column" spacing="8px">
            <LabelMediumBold data-testid={testId('hotel-name')} truncate>
              {name}
            </LabelMediumBold>
            {hasRating && (
              <RatingSummary
                size="small"
                ratingScore={ratingScore}
                reviewCount={ratingCount}
                recommendationRate={recommendationRate}
              />
            )}
          </Flex>
        }
        priceElement={
          <PriceTag
            totalPrice={totalPrice}
            totalPriceInternal={totalPriceInternal}
            totalPriceOriginal={totalPriceOriginal}
            totalDiscount={totalDiscount}
            totalDiscountOriginal={totalDiscountOriginal}
            pricePerPerson={pricePerPerson}
            hasCompactNotation
            discountType={discountType}
            discountPercentage={discountPercentage}
            totalDiscountHint={totalDiscountHint}
            canShowPriceCache={canShowPriceCache}
          />
        }
        ctaElement={
          <Button fullWidth data-testid={testId('hotelbox-cta')}>
            {t('mapView.hotelTileMedium.ctaButton')}
          </Button>
        }
      />
    );
  })
)``;
